import { escapeRegExp } from 'lodash'
import React, {
  FC,
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  MouseEvent,
} from 'react'
import { CellProps, Column, PluginHook, Renderer } from 'react-table'
import { I18n, Translate } from 'react-redux-i18n'
import { store } from '../index'
import { VariableSizeList } from 'react-window'
import {
  AssetGroup,
  PriceExtended,
  Signals,
  TradeObject,
  TradingAccount,
} from '@bdswiss/mt4-connector'
import Tooltip from 'react-tooltip'
import { TableContext, expandedReducer } from './contexts/TableContext'
import { setTradingSymbol } from '../actions/commonActions'
import { formatPrice } from '../lib/currency-formatter'
import { AssetPositionsTableContentContainer } from '../containers/AssetPositionTable'
import { VirtualizedTable } from './VirtualizedTable'
import { AssetSignalsTableContentContainer } from '../containers/AssetSignalsTable'
import { TradeMode } from '../types'
import AnalysisTrendsModalDialog from './AnalysisTrendsModalDialog'
import {getLeverageForSecurity} from "../utils/common-utils";

const FavouriteCell: Renderer<CellProps<
  PriceExtended,
  { favourite: boolean; confirmed: boolean; symbol: string }
>> = ({ value: { favourite, confirmed, symbol } }) => {
  const { onToggleFavourite } = useContext(TableContext)
  const toggleFavouriteHandle = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const { symbol } = e.currentTarget.dataset
      symbol && onToggleFavourite?.(symbol)
    },
    [onToggleFavourite]
  )

  return (
    <i
      onClick={toggleFavouriteHandle}
      className={`favourite ${favourite ? 'icon-star-full' : 'icon-star-empty'} ${
        !confirmed ? 'unconfirmed' : ''
      }`}
      title={I18n.t('favourite')}
      data-symbol={symbol}
      // data-cy={props.value}
    />
  )
}

const LeveragedLabelCell: Renderer<CellProps<
  PriceExtended,
  { security: string; leverage: number }
>> = ({ value: { security, leverage } }) => {
  return (
    security.startsWith('DMA') && (
      <span className="leveraged-label">
        {I18n.t(leverage > 1 ? 'leveragedLabel' : 'unleveragedLabel')}
      </span>
    )
  )
}

const SymbolCell: Renderer<CellProps<PriceExtended>> = ({ value: { label } }) => {
  if (!label) {
    return <div />
  }

  return (
    <div
      className="bdswiss-table--bold"
      title={label}
      // data-cy={label}
    >
      {label}
    </div>
  )
}

const DescriptionCell: Renderer<CellProps<PriceExtended>> = ({
  row: {
    original: { description },
  },
}) => {
  return <div title={description}>{description}</div>
}

const TypeCell: Renderer<CellProps<PriceExtended, string>> = ({ value }) => {
  return (
    <div title={value}>
      <Translate value={value} />
    </div>
  )
}

const PriceCell: Renderer<CellProps<PriceExtended>> = ({
  row: {
    original: { mbid, digits },
  },
}) => {
  const value = formatPrice(mbid, digits, '')
  return <div title={value}>{value}</div>
}

const ChangeCell: Renderer<CellProps<PriceExtended>> = ({ value: { change } }) => {
  return (
    <div className={'bdswiss-table--' + (change > 0 ? 'positive' : 'negative')}>
      {change.toFixed(2)}%&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    </div>
  )
}

export const AnalysisTrendsCell: Renderer<CellProps<
  PriceExtended & {
    ric: string
  }
>> = ({
  row: {
    original: { ric },
  },
}) => {
  const { onToggleAnalysisTrends } = useContext(TableContext)
  const clickHandle = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const { ric } = e.currentTarget.dataset
      ric && onToggleAnalysisTrends?.(ric)
    },
    [onToggleAnalysisTrends]
  )
  return (
    <div className={'btn-analysis-trends__wrap'}>
      <button
        onClick={clickHandle}
        type="button"
        className={'btn btn-analysis-trends btn-block ' + (!ric ? 'disabled' : '')}
        data-ric={ric}
        data-tip={!ric ? I18n.t('analysisNotAvailableTooltip') : ''}
        data-for={'analysisTrendsTooltip'}
      >
        <i className="icon-analysis-trends" />
      </button>
    </div>
  )
}

const SellCell: Renderer<CellProps<PriceExtended>> = ({
  row: {
    original: { symbol, trade, mbid },
  },
}) => {
  const setSymbol = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      store.dispatch(setTradingSymbol(symbol, 'sell'))
    },
    [symbol]
  )

  return (
    [TradeMode.enabled, TradeMode.onlySell].includes(trade) && (
      <button
        onClick={setSymbol}
        type="button"
        className="btn btn-outline-danger btn-block"
        // data-cy="assetSell"
      >
        <i className="icon-sell-new" />
        {mbid}
      </button>
    )
  )
}

const BuyCell: Renderer<CellProps<PriceExtended>> = ({
  row: {
    original: { symbol, trade, mask },
  },
}) => {
  const setSymbol = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      store.dispatch(setTradingSymbol(symbol, 'buy'))
    },
    [symbol]
  )

  return (
    [TradeMode.enabled, TradeMode.onlyBuy].includes(trade) && (
      <button
        onClick={setSymbol}
        type="button"
        className="btn btn-outline-success btn-block"
        // data-cy="assetBuy"
      >
        <i className="icon-buy-new" />
        {mask}
      </button>
    )
  )
}

const initialState: { [symbol: string]: boolean } = {}

export interface AssetsTableFCProps {
  account: TradingAccount
  accountType: string
  assetsData: any[]
  assetGroup: string
  assetGroups: AssetGroup[]
  symbol: string
  positions: TradeObject[]
  signals: Signals
  favourites: Map<string, { confirmed: boolean; operation: 'on' | 'off' }>
  filter?: string
  onToggleFavourite: (favourite: string) => void
}

const plugins: Array<PluginHook<{}>> = [
  hooks => {
    hooks.getCellProps.push((props, meta) => {
      switch (meta.cell.column.id) {
        case 'favourite':
          return { className: 'rt-td favourite-cell' }
        case 'leveraged-label':
          return { className: 'rt-td leveraged-label-cell' }
        case 'analysis-trends':
          return { className: 'rt-td analysis-trends-cell' }
      }

      return { className: 'rt-td' }
    })
  },
]

export const AssetsTableFC: FC<AssetsTableFCProps> = ({
  account,
  accountType,
  assetsData,
  assetGroup,
  assetGroups,
  symbol,
  positions,
  signals,
  favourites,
  filter,
  onToggleFavourite,
}) => {
  const columns = useMemo<Array<Column<any>>>(() => {
    const columns: Array<Column<any>> = [
      {
        Header: ' ',
        id: 'favourite',
        minWidth: 30,
        width: 30,
        accessor: ({ symbol }) => {
          const favourite = favourites.get(symbol)
          const confirmed = favourite ? favourite.confirmed : true
          return { favourite, confirmed, symbol: symbol }
        },
        Cell: FavouriteCell,
      },
      {
        Header: '',
        id: 'leveraged-label',
        minWidth: 22,
        width: 22,
        Cell: LeveragedLabelCell,
        accessor: (row) => {
          return {
            security: row.security,
            leverage: getLeverageForSecurity(account,row).toFixed(0),
          }
        },
      },
      {
        Header: 'name',
        id: 'label',
        accessor: ({ label }) => ({ label }),
        minWidth: 60,
        width: 70,
        Cell: SymbolCell,
      },
      {
        Header: 'description',
        id: 'description',
        accessor: ({ description }) => ({ description }),
        minWidth: 110,
        Cell: DescriptionCell,
      },
      {
        Header: 'type',
        id: 'type',
        accessor: ({ symbol }) => {
          const assetGroup = assetGroups
            .filter(group => group.label !== 'popular')
            .find(group => group.symbols.includes(symbol))

          return assetGroup?.label || ''
        },
        minWidth: 60,
        width: 80,
        Cell: TypeCell,
      },
      {
        Header: 'price',
        minWidth: 60,
        width: 80,
        accessor: ({ mbid }) => ({ mbid }),
        Cell: PriceCell,
      },
      {
        Header: '24h change',
        minWidth: 60,
        width: 70,
        accessor: ({ change }) => ({ change }),
        Cell: ChangeCell,
      },
      {
        Header: 'sell',
        minWidth: 50,
        width: 80,
        Cell: SellCell,
      },
      {
        Header: 'buy',
        minWidth: 50,
        width: 80,
        Cell: BuyCell,
      },
    ]

    if (accountType?.includes('MT5')) {
      columns.push({
        id: 'analysis-trends',
        minWidth: 35,
        width: 27,
        accessor: 'symbol',
        Cell: AnalysisTrendsCell,
      })
    }

    return columns
  }, [favourites, assetGroups, account.leverage, accountType])

  const data = useMemo(() => {
    return filter
      ? Object.values(assetsData).filter(({ symbol, description }) =>
          new RegExp(escapeRegExp(filter.replace('/', '')), 'i').test(
            [symbol, description].join('|')
          )
        )
      : Object.values(assetsData)
  }, [assetsData, filter])

  const [expandedPositionsState, expandedPositionsDispatch] = useReducer(
    expandedReducer,
    initialState
  )
  const getItemSize = useCallback(
    (index: number) => {
      const asset = data[index]
      const signalsSize = Object.values(signals).filter(s => s.symbol === asset.symbol).length
      const positionsSize = positions.filter(p => p.symbol === asset.symbol).length
      const size = assetGroup === 'favourites' ? signalsSize : positionsSize
      if (size === 0) {
        return 36
      }
      if (size <= 3) {
        return 36 + 22 + size * 36
      }
      return 36 + 22 + (expandedPositionsState[asset.symbol] ? size : 3) * 36 + 22
    },
    [data, positions, signals, assetGroup, expandedPositionsState]
  )
  const listRef = useRef<VariableSizeList>(null)
  useEffect(() => {
    if (listRef && listRef.current) {
      listRef.current.resetAfterIndex(0)
    }
  }, [listRef, positions.length])
  const currentSymbol = symbol
  const isRowActive = useCallback(symbol => symbol === currentSymbol, [currentSymbol])

  const rowClickHandle = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const { symbol } = e.currentTarget.dataset
      const asset = data.find(asset => asset.symbol === symbol)

      symbol &&
        asset?.trade > TradeMode.onlyClose &&
        store.dispatch(
          setTradingSymbol(
            symbol,
            [TradeMode.enabled, TradeMode.onlyBuy].includes(asset?.trade) ? 'buy' : 'sell'
          )
        )
    },
    [data]
  )

  const [selectedAnalysisTrendsSymbol, setSelectedAnalysisTrendsSymbol] = useState<string>('')

  const onToggleAnalysisTrends = useCallback((symbol: string) => {
    setSelectedAnalysisTrendsSymbol(symbol)
  }, [])

  const onCloseAnalysisTrendsDialog = useCallback(() => {
    setSelectedAnalysisTrendsSymbol('')
  }, [])

  return (
    <TableContext.Provider
      value={{
        keyPropertyName: 'symbol',
        onToggleFavourite,
        expandedPositionsDispatch,
        expandedPositionsState,
        listRef,
        isRowActive,
        rowClickHandle,
        onToggleAnalysisTrends,
      }}
    >
      <VirtualizedTable
        key={`autoSizer_${filter}`}
        columns={columns}
        data={data}
        SubComponent={
          assetGroup === 'favourites'
            ? AssetSignalsTableContentContainer
            : AssetPositionsTableContentContainer
        }
        getItemSize={getItemSize}
        plugins={plugins}
        className={'select-assets__table'}
      />
      {selectedAnalysisTrendsSymbol && (
        <AnalysisTrendsModalDialog
          symbol={selectedAnalysisTrendsSymbol}
          onClose={onCloseAnalysisTrendsDialog}
        />
      )}
      <Tooltip
        className={'tooltip'}
        id={'analysisTrendsTooltip'}
        effect="solid"
        type="light"
        place={'bottom'}
        delayShow={300}
        multiline={true}
      />
    </TableContext.Provider>
  )
}
